import * as analytics from "@client/shared/analytics";
import * as utils from "@client/utils";
import * as headless from "@headlessui/react";
import {classNames} from "@reactivated";
import {motion} from "motion/react";
import * as React from "react";

interface BaseButton {
    variant?: "indigo" | "text" | "outline";
    disabled?: boolean;
    expand?: boolean;
    children: React.ReactNode;
    className?: string;
    form?: string;
}

interface SubmitButton extends BaseButton {
    submit: true;
}

interface SubmitActionButton extends BaseButton {
    submit: true;
    name: string;
    value: string;
}

interface LinkButton extends BaseButton {
    href: string;
    track?: string;
}

interface ActionButton extends BaseButton {
    onClick: () => void;
}

type Props = SubmitButton | SubmitActionButton | LinkButton | ActionButton;

const buttonBase =
    "font-sans py-2.5 px-5 border-none flex w-min whitespace-nowrap text-sm rounded-md cursor-pointer justify-center items-center h-10";

const variantClasses = {
    primary:
        "bg-[#E2F4FB] text-[#181a1b] font-medium active:bg-[#4FAFE5] active:text-white hover:bg-[#4FAFE5] hover:text-white",
    indigo: "bg-[#52529C] text-white font-semibold hover:not-disabled:bg-[#4FAFE5] hover:not-disabled:text-white",
    text: "bg-transparent text-[#181a1b] font-semibold",
    outline: "bg-transparent text-[#181a1b] font-normal border border-[#D8D8D8]",
} as const;

export const Button = (props: Props) => {
    const variant = props.variant ?? "primary";

    const styling =
        props.className != null
            ? props.className
            : classNames(
                  buttonBase,
                  variantClasses[variant],
                  props.disabled == true && "opacity-40 cursor-default",
                  props.expand == true && "w-auto",
              );

    if ("href" in props) {
        const href = `${utils.getBaseURL()}${props.href}`;

        const onClick =
            props.track != null ? analytics.trackLink(props.track) : undefined;

        return (
            <a className={styling} onClick={onClick} href={href}>
                {props.children}
            </a>
        );
    } else {
        const handleOnClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
            if ("onClick" in props) {
                event.preventDefault();
                props.onClick();
            }
        };

        return (
            <button
                disabled={props.disabled ?? false}
                type={"submit" in props ? "submit" : "button"}
                form={props.form}
                onClick={handleOnClick}
                className={styling}
                name={"name" in props ? props.name : undefined}
                value={"value" in props ? props.value : undefined}
            >
                {props.children}
            </button>
        );
    }
};

export const ButtonGroup = (props: {children?: React.ReactNode}) => (
    <div className="-m-2.5 flex flex-wrap *:m-2.5">{props.children}</div>
);

export function AsyncButton(
    props: Omit<headless.ButtonProps, "children" | "className" | "onClick"> & {
        children: React.ReactNode;
        className?: string;
        forceLoading?: boolean;
        onClick?: (() => Promise<void>) | (() => void);
    },
) {
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [showSpinner, setShowSpinner] = React.useState(false);
    const spinnerTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const minimumDisplayTimerRef = React.useRef<NodeJS.Timeout | null>(null);
    const forceLoadingStartRef = React.useRef<number | null>(null);

    const loading = props.forceLoading == true || showSpinner;

    React.useEffect(() => {
        if (props.forceLoading == true && forceLoadingStartRef.current == null) {
            // Start force loading
            forceLoadingStartRef.current = Date.now();
            setShowSpinner(true);
        } else if (
            props.forceLoading == false &&
            forceLoadingStartRef.current != null
        ) {
            // End force loading - check minimum time
            const elapsed = Date.now() - forceLoadingStartRef.current;
            const minimumTime = 400;

            if (elapsed < minimumTime) {
                // Keep showing for minimum time
                setTimeout(() => {
                    setShowSpinner(false);
                    forceLoadingStartRef.current = null;
                }, minimumTime - elapsed);
            } else {
                setShowSpinner(false);
                forceLoadingStartRef.current = null;
            }
        }
    }, [props.forceLoading]);

    React.useEffect(() => {
        return () => {
            if (spinnerTimerRef.current) {
                clearTimeout(spinnerTimerRef.current);
            }
            if (minimumDisplayTimerRef.current) {
                clearTimeout(minimumDisplayTimerRef.current);
                // Ends the click's wait for the minimum spinner time.
                minimumDisplayTimerRef.current = null;
            }
        };
    }, []);

    return (
        <headless.Button
            aria-label={props["aria-label"]}
            type={props.type ?? "button"}
            style={props.style}
            onClick={async () => {
                if (isProcessing == true || props.forceLoading == true) {
                    return;
                }

                const promise = props.onClick?.();
                if (promise instanceof Promise) {
                    setIsProcessing(true);

                    // Delay showing spinner for 150ms
                    spinnerTimerRef.current = setTimeout(() => {
                        setShowSpinner(true);
                        // Once shown, keep it visible for at least 400ms
                        minimumDisplayTimerRef.current = setTimeout(() => {
                            minimumDisplayTimerRef.current = null;
                        }, 400);
                    }, 150);

                    // A rejected action still releases the button, so it can be retried.
                    try {
                        await promise;
                    } finally {
                        clearTimeout(spinnerTimerRef.current);
                        spinnerTimerRef.current = null;

                        // Wait for minimum display time if spinner was shown
                        if (minimumDisplayTimerRef.current) {
                            await new Promise((resolve) => {
                                const checkTimer = setInterval(() => {
                                    if (!minimumDisplayTimerRef.current) {
                                        clearInterval(checkTimer);
                                        resolve(undefined);
                                    }
                                }, 50);
                            });
                        }

                        setShowSpinner(false);
                        setIsProcessing(false);
                    }
                }
            }}
            disabled={
                props.disabled == true || isProcessing == true || showSpinner == true
            }
            className={classNames(props.className, "relative")}
        >
            <div className={classNames(loading && "invisible", "contents")}>
                {props.children}
            </div>
            {loading == true && (
                <div
                    className={classNames(
                        "absolute inset-0 flex justify-center items-center",
                    )}
                >
                    <motion.div
                        className="aspect-square h-[50%] max-h-7 rounded-full border-[2.5px] border-gray-400/70 border-t-current will-change-transform"
                        animate={{rotate: 360}}
                        transition={{
                            duration: 1,
                            repeat: Infinity,
                            ease: "linear",
                        }}
                    />
                </div>
            )}
        </headless.Button>
    );
}
